import { createSlice } from "@reduxjs/toolkit"
import {
  createDriver,
  createRole,
  createSupplier,
  deleteSupplier,
  fetchAllKaleUsers,
  fetchDrivers,
  fetchIdTypes,
  fetchRoles,
  fetchSupplierById,
  fetchSuppliers,
  kaleUser,
  partialUpdateSupplier,
  TotalCustomers,
  TotalKaleUsers,
  updateSupplier
} from "../actions/kale/kale-action"
import {
  blockUser,
  changeUserPassword,
  corporateLogin,
  corporateUnverifyUser,
  corporateVerification,
  createCorporateProfile,
  createCorporateUser,
  createUser,
  fetchAllSystemUsers,
  fetchBlockedUsers,
  fetchCorporateProfileById,
  fetchCorporateProfiles,
  fetchCorporateUsers,
  fetchUsersByBlockStatus,
  resetUserPassword,
  sendCorporateReminder,
  sendUserReminder,
  unblockUser,
  unverifyUser,
  updateCorporateProfile,
  updateCorporateUserProfile,
  updateUserProfile,
  updateUserVerification,
  userAuthenticationAction
} from "../actions/login/login-action"

const checkAuth = () => {
  const accessToken = localStorage.getItem("accessToken")
  const refreshToken = localStorage.getItem("refreshToken")
  return !!(accessToken && refreshToken)
}

const initialState = {
  tokens: {
    access: null,
    refresh: null
  },
  user: JSON.parse(localStorage.getItem("user")) || { user_type: "" },
  isAuthenticated: checkAuth(),
  systemUsers: {
    users: [],
    total_users: 0,
    unverified_users: 0,
    user_type_distribution: {}
  },
  corporateUser: {
    users: [],
    total_users: 0,
    unverified_users: 0
  },
  kaleUsers: [],
  suppliers: [],
  drivers: [],
  corporateProfiles: [],
  currentSupplier: null,
  profiles: [],
  currentProfile: null,
  idTypes: [],
  userRoles: [],
  assignRole: [],
  blockedUsers: [],
  filteredUsers: [],
  loading: false,
  success: false,
  error: false,
  message: "",
  errorCode: ""
}

const userManagementSlice = createSlice({
  name: "userMngmt",
  initialState,
  reducers: {
    initializeAuth: (state) => {
      const accessToken = localStorage.getItem("accessToken")
      const refreshToken = localStorage.getItem("refreshToken")
      const user = localStorage.getItem("user")
      if (accessToken && refreshToken && user) {
        state.tokens = { access: accessToken, refresh: refreshToken }
        state.user = JSON.parse(user)
        state.isAuthenticated = true
      }
    },
    updateTokens: (state, action) => {
      state.tokens.access = action.payload.access
      state.isAuthenticated = true
      localStorage.setItem("accessToken", action.payload.access)
    },
    initializeSystemUser: (state) => {
      return {
        ...initialState,
        user: state.user,
        suppliers: [],
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated
      }
    },
    logout: (state) => {
      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")
      localStorage.removeItem("user")
      return {
        ...initialState,
        isAuthenticated: false,
        user: { user_type: "" },
        tokens: { access: null, refresh: null }
      }
    }
  },
  extraReducers: (builder) => {
    builder

      .addCase(userAuthenticationAction.pending, (state) => {
        state.loading = true
        state.error = false
        state.success = false
        state.message = ""
      })
      .addCase(userAuthenticationAction.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.error = false
        state.user = action.payload.user
        state.tokens = action.payload.tokens
        state.isAuthenticated = true
        state.message = "Login successful"
        state.errorCode = action.payload?.code || null
        localStorage.setItem("accessToken", action.payload.tokens.access)
        localStorage.setItem("refreshToken", action.payload.tokens.refresh)
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...action.payload.user,
            last_login: new Date().toISOString()
          })
        )
      })
      .addCase(userAuthenticationAction.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = true
        state.message = action.payload?.message || "Login failed"
        state.isAuthenticated = false
        localStorage.removeItem("accessToken")
        localStorage.removeItem("refreshToken")
        localStorage.removeItem("user")
      })

      // Handle creating a new user
      .addCase(createUser.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = false
        state.message = ""
      })
      .addCase(createUser.fulfilled, (state, action) => {
        const newUser = action.payload?.data
        if (!state.systemUsers || typeof state.systemUsers !== "object") {
          state.systemUsers = {
            users: [],
            total_users: 0,
            unverified_users: 0,
            user_type_distribution: {}
          }
        }

        if (!Array.isArray(state.systemUsers.users)) {
          state.systemUsers.users = []
        }

        if (newUser) {
          state.systemUsers.users.push(newUser)
          state.systemUsers.total_users =
            (state.systemUsers.total_users || 0) + 1
          if (!newUser.is_verified) {
            state.systemUsers.unverified_users =
              (state.systemUsers.unverified_users || 0) + 1
          }
        }

        state.loading = false
        state.success = true
        state.error = false
        state.message =
          action.payload?.message ||
          "Account created successfully and pending approval"
      })

      .addCase(createUser.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = true
        state.message = action.payload?.message || "An error occurred"
      })

      // Handle fetching all system users
      .addCase(fetchAllSystemUsers.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(fetchAllSystemUsers.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        state.systemUsers = {
          total_users: action.payload.total_users,
          unverified_users: action.payload.unverified_users,
          user_type_distribution: action.payload.user_type_distribution,

          users: Array.isArray(action.payload) ? action.payload : []
        }
        state.message = ""
      })
      .addCase(fetchAllSystemUsers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.systemUsers = []
        state.message = action.payload?.message || "Failed to fetch users"
      })

      .addCase(updateUserVerification.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(updateUserVerification.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        const updatedUser = action.payload.data

        if (!state.systemUsers || typeof state.systemUsers !== "object") {
          state.systemUsers = {
            users: [],
            total_users: 0,
            unverified_users: 0,
            user_type_distribution: {}
          }
        }

        if (!Array.isArray(state.systemUsers.users)) {
          state.systemUsers.users = []
        }

        state.systemUsers.users = state.systemUsers.users.map((user) =>
          user.id === updatedUser.id
            ? { ...user, is_verified: updatedUser.is_verified }
            : user
        )

        const unverifiedCount = state.systemUsers.users.filter(
          (user) => !user.is_verified
        ).length
        state.systemUsers.unverified_users = unverifiedCount

        state.message =
          action.payload.message || "Verification status updated successfully"
      })
      .addCase(updateUserVerification.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message =
          action.payload?.message || "Failed to update verification status"
      })

      //handling kale users

      .addCase(kaleUser.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = false
        state.message = ""
      })

      .addCase(kaleUser.fulfilled, (state, action) => {
        const newKaleUser = action.payload
        state.kaleUsers = Array.isArray(state.kaleUsers)
          ? [...state.kaleUsers, newKaleUser]
          : [newKaleUser]
        state.loading = false
        state.success = true
        state.error = false
        state.message = "Kale User created successfully"
      })

      .addCase(kaleUser.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = true
        state.message = action.payload?.message || "An error occurred"
      })

      .addCase(fetchAllKaleUsers.pending, (state) => {
        state.loading = true
        state.error = null
        state.message = null
      })

      .addCase(fetchAllKaleUsers.fulfilled, (state, action) => {
        state.loading = false
        state.kaleUsers = action.payload
        state.error = null
        state.message = null
      })

      .addCase(fetchAllKaleUsers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to fetch users"
      })

      .addCase(TotalKaleUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(TotalKaleUsers.fulfilled, (state, action) => {
        state.loading = false
        state.kaleUsers = action.payload
        state.error = null
      })

      .addCase(TotalKaleUsers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message
      })

      .addCase(TotalCustomers.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(TotalCustomers.fulfilled, (state, action) => {
        state.loading = false
        state.systemUsers = action.payload
        state.error = null
      })

      .addCase(TotalCustomers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message
      })

      .addCase(createCorporateUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createCorporateUser.fulfilled, (state, action) => {
        state.loading = false

        state.user = {
          ...state.user,
          ...action.payload.data
        }

        if (action.payload.data?.user_type) {
          state.user.user_type = action.payload.data.user_type
        }
      })
      .addCase(createCorporateUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      //supplier

      .addCase(createSupplier.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = false
        state.message = ""
      })

      .addCase(createSupplier.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.error = false
        if (!Array.isArray(state.suppliers)) {
          state.suppliers = []
        }

        if (action.payload?.data) {
          state.suppliers.push(action.payload.data)
        }
        state.message =
          action.payload?.message || "Supplier created successfully"
      })

      .addCase(createSupplier.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = true
        state.message = action.payload?.message || "Failed to create supplier"
      })

      // Fetch all suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true
        state.error = false
      })

      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.suppliers = action.payload.data
        state.message = action.payload.message
      })

      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload.message
      })

      // Fetch supplier by ID
      .addCase(fetchSupplierById.pending, (state) => {
        state.loading = true
        state.error = false
      })

      .addCase(fetchSupplierById.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.currentSupplier = action.payload.data
        state.message = action.payload.message
      })

      .addCase(fetchSupplierById.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload.message
      })

      // Fetch ID types
      .addCase(fetchIdTypes.pending, (state) => {
        state.loading = true
        state.error = false
      })

      .addCase(fetchIdTypes.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.idTypes = action.payload.data
        state.message = action.payload.message
      })

      .addCase(fetchIdTypes.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload.message
      })

      // Update supplier
      .addCase(updateSupplier.pending, (state) => {
        state.loading = true
        state.error = false
      })

      .addCase(updateSupplier.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.suppliers = state.suppliers.map((supplier) =>
          supplier.id === action.payload.data.id
            ? action.payload.data
            : supplier
        )
        state.message = action.payload.message
      })

      .addCase(updateSupplier.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload.message
      })

      // Partial update supplier
      .addCase(partialUpdateSupplier.pending, (state) => {
        state.loading = true
        state.error = false
      })

      .addCase(partialUpdateSupplier.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.suppliers = state.suppliers.map((supplier) =>
          supplier.id === action.payload.data.id
            ? action.payload.data
            : supplier
        )
        state.message = action.payload.message
      })

      .addCase(partialUpdateSupplier.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload.message
      })

      //deleting
      .addCase(deleteSupplier.pending, (state) => {
        state.loading = true
        state.error = false
      })

      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.suppliers = state.suppliers.filter(
          (supplier) => supplier.id !== action.payload.id
        )
        state.message = action.payload.message
      })

      .addCase(deleteSupplier.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload.message
      })

      .addCase(fetchRoles.pending, (state) => {
        state.loading = true
      })

      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false
        state.userRoles = action.payload.userRoles || []
        state.error = null
      })

      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false
        state.userRoles = []
      })

      .addCase(createRole.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(createRole.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        if (state.userRoles) {
          state.userRoles.push(action.payload.role)
        } else {
          state.userRoles = [action.payload.role]
        }
      })

      .addCase(createRole.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || "Failed to create role"
      })

      .addCase(createDriver.pending, (state) => {
        state.loading = true
        state.error = null
      })

      .addCase(createDriver.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.error = false
        if (!Array.isArray(state.drivers)) {
          state.drivers = []
        }

        if (action.payload?.data) {
          state.drivers.push(action.payload.data)
        }
        state.message = action.payload?.message || "Driver created successfully"
      })

      .addCase(createDriver.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = true
        state.message = action.payload?.message || "Failed to create supplier"
      })

      .addCase(fetchDrivers.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.drivers = Array.isArray(action.payload.data)
          ? action.payload.data
          : []
        state.message = action.payload.message
      })

      .addCase(fetchDrivers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload.message
      })

      .addCase(updateCorporateProfile.pending, (state) => {
        state.loading = true
        state.error = null
        state.message = ""
      })
      .addCase(updateCorporateProfile.fulfilled, (state, action) => {
        state.loading = false
        state.success = true

        if (state.user?.corporate_profile?.id === action.payload.data.id) {
          state.user = {
            ...state.user,
            corporate_profile: action.payload.data
          }

          const storedUser = JSON.parse(localStorage.getItem("user") || "{}")
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...storedUser,
              corporate_profile: action.payload.data
            })
          )
        }

        state.message =
          action.payload.message || "Corporate profile updated successfully"
      })
      .addCase(updateCorporateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.success = false
        state.message =
          action.payload?.message || "Failed to update corporate profile"
      })

      .addCase(updateCorporateUserProfile.pending, (state) => {
        state.loading = true
        state.error = null
        state.message = ""
      })
      .addCase(updateCorporateUserProfile.fulfilled, (state, action) => {
        state.loading = false
        state.success = true

        // Store the updated profile data
        if (state.user && action.payload.data) {
          // Update the corporate_user_profile in user object
          state.user.corporate_user_profile = action.payload.data

          // Update the main user object with key fields
          if (action.payload.data.position) {
            state.user.position = action.payload.data.position
          }

          // Most importantly: Update the corporate profile in corporateProfiles array
          if (
            Array.isArray(state.corporateProfiles) &&
            state.corporateProfiles.length > 0
          ) {
            const profileId =
              typeof state.user.corporate_profile === "number"
                ? state.user.corporate_profile
                : null

            if (profileId) {
              const profileIndex = state.corporateProfiles.findIndex(
                (p) => p.id === profileId
              )
              if (profileIndex !== -1) {
                // Update fields in the corporate profile that match user profile fields
                const updatedData = action.payload.data
                if (updatedData.company_name)
                  state.corporateProfiles[profileIndex].company_name =
                    updatedData.company_name
                if (updatedData.tax_id)
                  state.corporateProfiles[profileIndex].tax_id =
                    updatedData.tax_id
                if (updatedData.address)
                  state.corporateProfiles[profileIndex].address =
                    updatedData.address
                if (updatedData.position)
                  state.corporateProfiles[profileIndex].position =
                    updatedData.position
                if (updatedData.registration_number)
                  state.corporateProfiles[profileIndex].registration_number =
                    updatedData.registration_number
                if (updatedData.industry_type)
                  state.corporateProfiles[profileIndex].industry_type =
                    updatedData.industry_type

                // Update timestamp
                state.corporateProfiles[profileIndex].updated_at =
                  new Date().toISOString()
              }
            }
          }
        }

        state.message =
          action.payload.message ||
          "Corporate user profile updated successfully"
      })
      .addCase(updateCorporateUserProfile.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.success = false
        state.message =
          action.payload?.message || "Failed to update corporate user profile"
      })

      .addCase(fetchCorporateProfiles.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(fetchCorporateProfiles.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.corporateProfiles = action.payload
      })
      .addCase(fetchCorporateProfiles.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.corporateProfiles = []
        state.message =
          action.payload?.message || "Failed to fetch corporate profiles"
      })

      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        if (state.systemUsers && Array.isArray(state.systemUsers.users)) {
          state.systemUsers.users = state.systemUsers.users.map((user) =>
            user.id === action.payload.data.id
              ? { ...user, ...action.payload.data }
              : user
          )
        }

        if (state.user && state.user.id === action.payload.data.id) {
          state.user = { ...state.user, ...action.payload.data }
          localStorage.setItem("user", JSON.stringify(state.user))
        }

        state.message = action.payload.message || "Profile updated successfully"
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to update profile"
      })

      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(changeUserPassword.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.message =
          action.payload.message || "Password changed successfully"
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to change password"
      })

      .addCase(unverifyUser.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(unverifyUser.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        if (state.systemUsers && Array.isArray(state.systemUsers.users)) {
          state.systemUsers.users = state.systemUsers.users.map((user) =>
            user.id === action.payload.data.id
              ? { ...user, is_verified: false }
              : user
          )

          state.systemUsers.unverified_users = state.systemUsers.users.filter(
            (user) => !user.is_verified
          ).length
        }

        state.message = action.payload.message || "User unverified successfully"
      })
      .addCase(unverifyUser.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to unverify user"
      })

      .addCase(resetUserPassword.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(resetUserPassword.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.message = action.payload.message || "Password reset successfully"
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to reset password"
      })

      .addCase(corporateLogin.pending, (state) => {
        state.loading = true
        state.error = false
        state.success = false
        state.message = ""
      })
      .addCase(corporateLogin.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.error = false
        state.user = action.payload.user
        state.tokens = action.payload.tokens
        state.isAuthenticated = true
        state.message = ""
        state.errorCode = action.payload?.code || null
        localStorage.setItem("accessToken", action.payload.tokens.access)
        localStorage.setItem("refreshToken", action.payload.tokens.refresh)
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...action.payload.user,
            last_login: new Date().toISOString()
          })
        )
      })
      .addCase(corporateLogin.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = true
        state.message = action.payload?.message || "Corporate login failed"
        state.isAuthenticated = false
        localStorage.removeItem("accessToken")
        localStorage.removeItem("refreshToken")
        localStorage.removeItem("user")
      })

      .addCase(fetchCorporateUsers.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(fetchCorporateUsers.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        state.corporateUser = {
          total_users: Array.isArray(action.payload)
            ? action.payload.length
            : 0,
          users: Array.isArray(action.payload) ? action.payload : []
        }
        state.message = ""
      })
      .addCase(fetchCorporateUsers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.systemUsers = []
        state.message = action.payload?.message || "Failed to fetch users"
      })

      .addCase(createCorporateProfile.pending, (state) => {
        state.loading = true
        state.error = null
        state.message = ""
      })
      .addCase(createCorporateProfile.fulfilled, (state, action) => {
        state.loading = false
        state.success = true

        state.user = {
          ...state.user,
          corporate_profile: action.payload.data
        }

        const storedUser = JSON.parse(localStorage.getItem("user") || "{}")
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...storedUser,
            corporate_profile: action.payload.data
          })
        )

        state.message =
          action.payload.message || "Corporate profile created successfully"
      })
      .addCase(createCorporateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.success = false
        state.message =
          action.payload?.message || "Failed to create corporate profile"
      })

      .addCase(fetchCorporateProfileById.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(fetchCorporateProfileById.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        state.currentProfile = action.payload
        state.error = false
      })
      .addCase(fetchCorporateProfileById.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message =
          action.payload?.message || "Failed to fetch corporate profile"
      })

      .addCase(corporateVerification.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(corporateVerification.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        const updatedUser = action.payload?.data

        if (!state.corporateUser || typeof state.corporateUser !== "object") {
          state.corporateUser = {
            users: [],
            total_users: 0,
            unverified_users: 0
          }
        }

        if (!Array.isArray(state.corporateUser.users)) {
          state.corporateUser.users = []
        }

        if (updatedUser && updatedUser.id) {
          state.corporateUser.users = state.corporateUser.users.map((user) => {
            if (user.id === updatedUser.id) {
              return { ...user, is_verified: true }
            }
            return user
          })

          const unverifiedCount = state.corporateUser.users.filter(
            (user) => !user.is_verified
          ).length
          state.corporateUser.unverified_users = unverifiedCount
        }

        state.message =
          action.payload?.message || "Verification status updated successfully"
      })
      .addCase(corporateVerification.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message =
          action.payload?.message || "Failed to update verification status"
      })

      .addCase(corporateUnverifyUser.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(corporateUnverifyUser.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        const updatedUser = action.payload?.data

        if (!state.corporateUser || typeof state.corporateUser !== "object") {
          state.corporateUser = {
            users: [],
            total_users: 0,
            unverified_users: 0
          }
        }

        if (!Array.isArray(state.corporateUser.users)) {
          state.corporateUser.users = []
        }

        if (updatedUser && updatedUser.id) {
          state.corporateUser.users = state.corporateUser.users.map((user) => {
            if (user.id === updatedUser.id) {
              return { ...user, is_verified: false }
            }
            return user
          })

          const unverifiedCount = state.corporateUser.users.filter(
            (user) => !user.is_verified
          ).length
          state.corporateUser.unverified_users = unverifiedCount
        }

        state.message =
          action.payload?.message || "User unverified successfully"
      })
      .addCase(corporateUnverifyUser.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message =
          action.payload?.message || "Failed to unverify corporate user"
      })

      .addCase(blockUser.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(blockUser.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        if (state.systemUsers && Array.isArray(state.systemUsers.users)) {
          state.systemUsers.users = state.systemUsers.users.map((user) =>
            user.id === action.payload.data.id
              ? {
                  ...user,
                  is_blocked: true,
                  block_reason: action.payload.data.block_reason
                }
              : user
          )
        }

        state.message = action.payload.message || "User blocked successfully"
      })
      .addCase(blockUser.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to block user"
      })

      .addCase(unblockUser.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(unblockUser.fulfilled, (state, action) => {
        state.loading = false
        state.error = false

        if (state.systemUsers && Array.isArray(state.systemUsers.users)) {
          state.systemUsers.users = state.systemUsers.users.map((user) =>
            user.id === action.payload.data.id
              ? { ...user, is_blocked: false, block_reason: null }
              : user
          )
        }

        state.message = action.payload.message || "User unblocked successfully"
      })
      .addCase(unblockUser.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to unblock user"
      })

      .addCase(fetchBlockedUsers.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(fetchBlockedUsers.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.blockedUsers = Array.isArray(action.payload) ? action.payload : []
      })
      .addCase(fetchBlockedUsers.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message =
          action.payload?.message || "Failed to fetch blocked users"
      })

      .addCase(sendUserReminder.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(sendUserReminder.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.message = action.payload.message || "Reminder sent successfully"
      })
      .addCase(sendUserReminder.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to send reminder"
      })

      .addCase(sendCorporateReminder.pending, (state) => {
        state.loading = true
        state.error = false
        state.message = ""
      })
      .addCase(sendCorporateReminder.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.message =
          action.payload.message || "Corporate reminder sent successfully"
      })
      .addCase(sendCorporateReminder.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message =
          action.payload?.message || "Failed to send corporate reminder"
      })

      .addCase(fetchUsersByBlockStatus.pending, (state) => {
        state.loading = true
        state.error = false
      })
      .addCase(fetchUsersByBlockStatus.fulfilled, (state, action) => {
        state.loading = false
        state.error = false
        state.filteredUsers = Array.isArray(action.payload)
          ? action.payload
          : []
      })
      .addCase(fetchUsersByBlockStatus.rejected, (state, action) => {
        state.loading = false
        state.error = true
        state.message = action.payload?.message || "Failed to fetch users"
      })
  }
})

export const { initializeSystemUser, logout, updateTokens, initializeAuth } =
  userManagementSlice.actions
export default userManagementSlice.reducer
